import {
	SELECTED_MAP_LAYERS,
	SELECTED_MAP_STYLE,
	SELECTED_MAP_SOURCES,
	SELECTED_MAPBOX_STYLE,
	SELECTED_SEARCH_TYPE,
	LIVE_TRAFFIC_COLUMNS,
	OPS_TRAFFIC_COLUMNS,
	SGI_WAIVER_OPERATOR_ADDRESS,
	AIRSPACE_MAP_BOUNDS,
	AIRSPACE_MAP_PIN,
	OPERATING_PICTURE_COLUMNS,
	TIME_FORMAT,
	DATE_FORMAT,
	UNITS,
	SELECTED_BASEMAP,
	OPS_VIEW_SELECTED_MAP_LAYERS,
	OWNSHIP_RING1,
	OWNSHIP_RING2,
	OWNSHIP_RING3,
	LIVE_TRAFFIC_METRIC_SPEED_UNIT,
	LIVE_TRAFFIC_IMPERIAL_SPEED_UNIT,
	LIVE_TRAFFIC_MAX_ALTITUDE_IMPERIAL,
	LIVE_TRAFFIC_MAX_ALTITUDE_METRIC,
	LIVE_TRAFFIC_LOW_ALTITUDE_COLOR,
	LIVE_TRAFFIC_MID_ALTITUDE_COLOR,
	LIVE_TRAFFIC_HIGH_ALTITUDE_COLOR,
	LIVE_TRAFFIC_ALTITUDE_THRESHOLDS_METRIC,
	LIVE_TRAFFIC_ALTITUDE_THRESHOLDS_IMPERIAL
} from 'constants/localStorageConstants';
import { defaultMapStyle, defaultMapboxStyleValue } from 'constants/mapConstants';
import {
	getLocationSearchOption,
	ownshipRing1Default,
	ownshipRing2Default,
	ownshipRing3Default,
	liveTrafficMetricSpeedUnitDefault,
	liveTrafficImperialSpeedUnitDefault
} from 'components/airSpace/airspaceConstants';
import {
	parseDateFormatFromAPI,
	parseTimeFormatFromAPI,
	defaultTimeFormat,
	defaultDateFormat,
	defaultUnits
} from 'components/accountSettings/accountSettingsConstants';
import { $lowAltitudeColor, $midAltitudeColor, $highAltitudeColor } from 'constants/styles/_colors';
import { setUnits } from 'reducers/globalSlice';

export const saveSelectedMapLayersToLS = layers => {
	localStorage.setItem(SELECTED_MAP_LAYERS, JSON.stringify(layers));
};

export const getSelectedMapLayersFromLS = () => {
	const layerSettings = localStorage.getItem(SELECTED_MAP_LAYERS);
	return layerSettings ? JSON.parse(layerSettings) : {};
};

export const saveSelectedMapSourcesToLS = layers => {
	localStorage.setItem(SELECTED_MAP_SOURCES, JSON.stringify(layers));
};

export const getSelectedMapSourcesFromLS = () => {
	const sourceSettings = localStorage.getItem(SELECTED_MAP_SOURCES);
	return sourceSettings ? JSON.parse(sourceSettings) : {};
};

export const saveSelectedMapStyleToLS = styleId => {
	localStorage.setItem(SELECTED_MAP_STYLE, styleId);
};

export const getSelectedMapStyleFromLS = () => {
	const selectedMapStyle = localStorage.getItem(SELECTED_MAP_STYLE);
	return selectedMapStyle ? selectedMapStyle : defaultMapStyle;
};

export const saveSelectedMapboxStyleToLS = styleId => {
	localStorage.setItem(SELECTED_MAPBOX_STYLE, styleId);
};

export const getSelectedMapBoxStyleFromLS = () => {
	const selectedMapStyle = localStorage.getItem(SELECTED_MAPBOX_STYLE);
	return selectedMapStyle ? selectedMapStyle : defaultMapboxStyleValue;
};

export const saveSelectedSearchTypeToLS = searchType => {
	localStorage.setItem(SELECTED_SEARCH_TYPE, JSON.stringify(searchType));
};

export const getSelectedSearchTypeFromLS = () => {
	const selectedSearchType = localStorage.getItem(SELECTED_SEARCH_TYPE);
	return selectedSearchType ? JSON.parse(selectedSearchType) : getLocationSearchOption();
};

export const getLiveTrafficColumnsFromLS = () => {
	const liveTrafficColumns = localStorage.getItem(LIVE_TRAFFIC_COLUMNS);
	return liveTrafficColumns ? JSON.parse(liveTrafficColumns) : [];
};

export const saveLiveTrafficColumnsToLS = columns => {
	localStorage.setItem(LIVE_TRAFFIC_COLUMNS, JSON.stringify(columns));
};

export const getOpsViewTrafficColumnsFromLS = () => {
	const liveTrafficColumns = localStorage.getItem(OPS_TRAFFIC_COLUMNS);
	return liveTrafficColumns ? JSON.parse(liveTrafficColumns) : [];
};

export const saveOpsViewTrafficColumnsToLS = columns => {
	localStorage.setItem(OPS_TRAFFIC_COLUMNS, JSON.stringify(columns));
};

export const saveSGIWaiverOperatorAddressToLS = address => {
	localStorage.setItem(SGI_WAIVER_OPERATOR_ADDRESS, address);
};

export const getSGIWaiverOperatorAddressFromLS = () => {
	const savedAddress = localStorage.getItem(SGI_WAIVER_OPERATOR_ADDRESS);
	return savedAddress ? savedAddress : '';
};

export const saveAirspaceMapBoundsToLS = bounds => {
	localStorage.setItem(AIRSPACE_MAP_BOUNDS, JSON.stringify(bounds));
};

export const getAirspaceMapBoundsFromLS = () => {
	const savedBounds = localStorage.getItem(AIRSPACE_MAP_BOUNDS);
	return savedBounds ? JSON.parse(savedBounds) : null;
};

export const saveAirspaceMapPinToLS = pin => {
	localStorage.setItem(AIRSPACE_MAP_PIN, JSON.stringify(pin));
};

export const getAirspaceMapPinFromLS = () => {
	const savedPin = localStorage.getItem(AIRSPACE_MAP_PIN);
	return savedPin ? JSON.parse(savedPin) : null;
};

export const getOperatingPictureColumnsFromLS = () => {
	const operatingPictureCols = localStorage.getItem(OPERATING_PICTURE_COLUMNS);
	return operatingPictureCols ? JSON.parse(operatingPictureCols) : [];
};

export const saveOperatingPictureColumnsToLS = columns => {
	localStorage.setItem(OPERATING_PICTURE_COLUMNS, JSON.stringify(columns));
};

export const saveTimeFormatToLS = timeFormat => {
	localStorage.setItem(TIME_FORMAT, timeFormat);
};

export const getTimeFormatFromLS = () => {
	const savedTimeFormat = localStorage.getItem(TIME_FORMAT);
	return savedTimeFormat ? savedTimeFormat : defaultTimeFormat;
};

export const saveDateFormatToLS = dateFormat => {
	localStorage.setItem(DATE_FORMAT, dateFormat);
};

export const getDateFormatFromLS = () => {
	const savedDateFormat = localStorage.getItem(DATE_FORMAT);
	return savedDateFormat ? savedDateFormat : defaultDateFormat;
};

export const getUnitsFromLS = () => {
	const savedUnits = localStorage.getItem(UNITS);
	return savedUnits ? savedUnits : defaultUnits;
};

export const saveUnitsToLS = units => {
	localStorage.setItem(UNITS, units);
};

export const setAccountSettingsToLS =
	(settings = {}) =>
	dispatch => {
		const units = settings.default_units ? settings.default_units : defaultUnits;
		const dateFormat = settings.default_date_format
			? parseDateFormatFromAPI(settings.default_date_format)
			: defaultDateFormat;
		const timeFormat = settings.default_time_format
			? parseTimeFormatFromAPI(settings.default_time_format)
			: defaultTimeFormat;

		// Only set if values don't exist in localStorage
		if (!localStorage.getItem(UNITS)) {
			saveUnitsToLS(units);
			dispatch(setUnits(units));
		}
		if (!localStorage.getItem(DATE_FORMAT)) {
			saveDateFormatToLS(dateFormat);
		}
		if (!localStorage.getItem(TIME_FORMAT)) {
			saveTimeFormatToLS(timeFormat);
		}
	};

export const saveSelectedBasemapToLS = basemapId => {
	localStorage.setItem(SELECTED_BASEMAP, basemapId);
};

export const getSelectedBasemapFromLS = () => {
	const selectedBasemap = localStorage.getItem(SELECTED_BASEMAP);
	return selectedBasemap ? selectedBasemap : defaultMapStyle;
};

export const opsViewSaveSelectedMapLayersToLS = layers => {
	localStorage.setItem(OPS_VIEW_SELECTED_MAP_LAYERS, JSON.stringify(layers));
};

export const opsViewGetSelectedMapLayersFromLS = () => {
	const layerSettings = localStorage.getItem(OPS_VIEW_SELECTED_MAP_LAYERS);
	return layerSettings ? JSON.parse(layerSettings) : {};
};

export const saveOwnshipRing1ToLS = ring => {
	localStorage.setItem(OWNSHIP_RING1, JSON.stringify(ring));
};

export const getOwnshipRing1FromLS = () => {
	const savedRing = localStorage.getItem(OWNSHIP_RING1);
	return savedRing ? JSON.parse(savedRing) : ownshipRing1Default;
};

export const saveOwnshipRing2ToLS = ring => {
	localStorage.setItem(OWNSHIP_RING2, JSON.stringify(ring));
};

export const getOwnshipRing2FromLS = () => {
	const savedRing = localStorage.getItem(OWNSHIP_RING2);
	return savedRing ? JSON.parse(savedRing) : ownshipRing2Default;
};

export const saveOwnshipRing3ToLS = ring => {
	localStorage.setItem(OWNSHIP_RING3, JSON.stringify(ring));
};

export const getOwnshipRing3FromLS = () => {
	const savedRing = localStorage.getItem(OWNSHIP_RING3);
	return savedRing ? JSON.parse(savedRing) : ownshipRing3Default;
};

export const saveLiveTrafficMetricSpeedUnitToLS = groundSpeed => {
	localStorage.setItem(LIVE_TRAFFIC_METRIC_SPEED_UNIT, JSON.stringify(groundSpeed));
};

export const getLiveTrafficMetricSpeedUnitFromLS = () => {
	const savedGroundSpeed = localStorage.getItem(LIVE_TRAFFIC_METRIC_SPEED_UNIT);
	return savedGroundSpeed ? JSON.parse(savedGroundSpeed) : liveTrafficMetricSpeedUnitDefault;
};

export const saveLiveTrafficImperialSpeedUnitToLS = groundSpeed => {
	localStorage.setItem(LIVE_TRAFFIC_IMPERIAL_SPEED_UNIT, JSON.stringify(groundSpeed));
};

export const getLiveTrafficImperialSpeedUnitFromLS = () => {
	const savedGroundSpeed = localStorage.getItem(LIVE_TRAFFIC_IMPERIAL_SPEED_UNIT);
	return savedGroundSpeed ? JSON.parse(savedGroundSpeed) : liveTrafficImperialSpeedUnitDefault;
};

export const saveLiveTrafficMaxAltitudeToLS = (maxAltitude, units) => {
	if (units !== 'imperial' && units !== 'metric') {
		return;
	}

	const key =
		units === 'imperial' ? LIVE_TRAFFIC_MAX_ALTITUDE_IMPERIAL : LIVE_TRAFFIC_MAX_ALTITUDE_METRIC;

	localStorage.setItem(key, JSON.stringify(maxAltitude));
};
export const getLiveTrafficMaxAltitudeImperialFromLS = () => {
	const imperialRaw = localStorage.getItem(LIVE_TRAFFIC_MAX_ALTITUDE_IMPERIAL);
	return imperialRaw ? JSON.parse(imperialRaw) : null;
};
export const getLiveTrafficMaxAltitudeMetricFromLS = () => {
	const metricRaw = localStorage.getItem(LIVE_TRAFFIC_MAX_ALTITUDE_METRIC);
	return metricRaw ? JSON.parse(metricRaw) : null;
};
export const getLiveTrafficMaxAltitudeFromLS = () => {
	return {
		imperial: getLiveTrafficMaxAltitudeImperialFromLS(),
		metric: getLiveTrafficMaxAltitudeMetricFromLS()
	};
};

export const saveLiveTrafficLowAltitudeColorToLS = color => {
	localStorage.setItem(LIVE_TRAFFIC_LOW_ALTITUDE_COLOR, color);
};
export const saveLiveTrafficMidAltitudeColorToLS = color => {
	localStorage.setItem(LIVE_TRAFFIC_MID_ALTITUDE_COLOR, color);
};
export const saveLiveTrafficHighAltitudeColorToLS = color => {
	localStorage.setItem(LIVE_TRAFFIC_HIGH_ALTITUDE_COLOR, color);
};

export const getLiveTrafficLowAltitudeColorFromLS = () => {
	const saved = localStorage.getItem(LIVE_TRAFFIC_LOW_ALTITUDE_COLOR);
	return saved || $lowAltitudeColor;
};
export const getLiveTrafficMidAltitudeColorFromLS = () => {
	const saved = localStorage.getItem(LIVE_TRAFFIC_MID_ALTITUDE_COLOR);
	return saved || $midAltitudeColor;
};
export const getLiveTrafficHighAltitudeColorFromLS = () => {
	const saved = localStorage.getItem(LIVE_TRAFFIC_HIGH_ALTITUDE_COLOR);
	return saved || $highAltitudeColor;
};

const defaultAltitudeThresholdsMetric = { low: 300, high: 1500 };
const defaultAltitudeThresholdsImperial = { low: 1000, high: 5000 };

export const saveLiveTrafficAltitudeThresholdsMetricToLS = ({ low, high }) => {
	localStorage.setItem(LIVE_TRAFFIC_ALTITUDE_THRESHOLDS_METRIC, JSON.stringify({ low, high }));
};
export const saveLiveTrafficAltitudeThresholdsImperialToLS = ({ low, high }) => {
	localStorage.setItem(LIVE_TRAFFIC_ALTITUDE_THRESHOLDS_IMPERIAL, JSON.stringify({ low, high }));
};

export const getLiveTrafficAltitudeThresholdsMetricFromLS = () => {
	const raw = localStorage.getItem(LIVE_TRAFFIC_ALTITUDE_THRESHOLDS_METRIC);
	return raw ? JSON.parse(raw) : defaultAltitudeThresholdsMetric;
};
export const getLiveTrafficAltitudeThresholdsImperialFromLS = () => {
	const raw = localStorage.getItem(LIVE_TRAFFIC_ALTITUDE_THRESHOLDS_IMPERIAL);
	return raw ? JSON.parse(raw) : defaultAltitudeThresholdsImperial;
};
